/**
 * App shell header — product bar layout + typography (plain objects for inline styles).
 * Three zones: left (brand), center (optional meta), right (account actions).
 */

import { foundation as f } from './tokens';
import { components, structure } from './theme/componentTokens';
import { semantic } from './theme/semanticTokens';
import { SAFE_TOP_FLOOR_DEFAULT_PX, SAFE_TOP_FLOOR_IOS_PX } from '../utils/ui/safeTopFloor';

const { color, spacePx, shadow, fontSize, fontWeight, lineHeight, fontFamily } = f;

/**
 * Minimum padding-top for mobile product chrome on iOS (every role: admin,
 * worker, supervisor, billing, platform). Sized to a normal iPhone status bar
 * (~47-50px) plus a thin buffer. Single source of truth: `SAFE_TOP_FLOOR_IOS_PX`.
 */
export const MOBILE_SHELL_SAFE_TOP_MIN = `${SAFE_TOP_FLOOR_IOS_PX}px`;

/**
 * CSS length for sticky/fixed shell headers. Pass token rhythm (e.g. spacePx[2])
 * or a literal like `'14px'` (billing shell). Default rhythm is 6px — closer to
 * iOS native compact-title spacing where content sits just below the safe area.
 *
 * Floor logic stays data-driven HERE: `--cvx-safe-top-floor` is set on `:root`
 * by `applySafeTopFloor` (src/utils/ui/safeTopFloor.js) before React mounts and
 * on orientation settle. That helper owns platform/mode/orientation rules
 * (iPhone browser → 6px floor; iPhone PWA portrait → 48px; etc.). This CSS
 * expression always clears a real inset via
 * `max(calc(rhythm + env(safe-area-inset-top)), var(--cvx-safe-top-floor))` —
 * do not reintroduce a standalone/browser branch in this file.
 */
export function cssMobileShellHeaderPaddingTop(rhythmPx = '6px') {
  return `max(calc(${rhythmPx} + env(safe-area-inset-top, 0px)), var(--cvx-safe-top-floor, ${SAFE_TOP_FLOOR_DEFAULT_PX}px))`;
}

/** Full-width header row: balanced proportions, premium vertical rhythm */
export const shellHeaderRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacePx[4],
  minHeight: '44px',
  boxSizing: 'border-box',
};

export const shellHeaderZoneLeft = {
  display: 'flex',
  alignItems: 'center',
  gap: spacePx[4],
  minWidth: 0,
  flex: '0 1 auto',
};

export const shellHeaderZoneCenter = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minWidth: 0,
  paddingLeft: spacePx[3],
  paddingRight: spacePx[3],
};

export const shellHeaderZoneRight = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexShrink: 0,
  gap: spacePx[2],
};

/** Single-line product wordmark */
export const shellProductName = {
  margin: 0,
  padding: 0,
  color: color.neutral.text,
  fontFamily: fontFamily.sans,
  fontSize: fontSize.md,
  fontWeight: fontWeight.semibold,
  lineHeight: lineHeight.tight,
  letterSpacing: '-0.02em',
  whiteSpace: 'nowrap',
};

/** Inline meta (company · type) — quiet, on baseline */
export const shellMetaGroup = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: `${spacePx[1]} ${spacePx[2]}`,
  minWidth: 0,
  maxWidth: '100%',
  fontFamily: fontFamily.sans,
};

export const shellMetaSeparator = {
  color: color.neutral.textMuted,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.normal,
  userSelect: 'none',
};

export const shellMetaType = {
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  color: color.neutral.textMuted,
  whiteSpace: 'nowrap',
};

/**
 * Industry / type label beside company — matches admin `headerMetaType`
 * (uppercase, tracked).
 */
export const shellHeaderMetaType = {
  color: color.neutral.textMuted,
  fontSize: '0.75rem',
  fontWeight: fontWeight.normal,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

/**
 * Desktop: company meta pinned DEAD-CENTER of the header row, independent of the
 * page-title and role-chip widths — mirrors AdminShellHeader's `headerMetaAbsolute`.
 * An in-flow meta row is a flex child of a space-between row, so
 * it drifts left/right (and visibly jitters on tab switch) as the title grows; this
 * anchors it absolutely so it never moves. REQUIRES the header row to be
 * position:relative, and the left/right zones to sit at a higher z-index so a very
 * long company name tucks behind them rather than overlapping.
 */
export const shellHeaderMetaCentered = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'inline-flex',
  alignItems: 'baseline',
  justifyContent: 'center',
  gap: '4px',
  minWidth: 0,
  maxWidth: 'min(560px, 52vw)',
  overflow: 'hidden',
  zIndex: 1,
};

/** Mobile: company row under page title — matches admin `headerMetaGroup` gap */
export const shellHeaderMetaGroupMobile = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'nowrap',
  gap: '4px',
  minWidth: 0,
  maxWidth: '100%',
  fontFamily: fontFamily.sans,
};

/** Header chrome: light surface, hairline edge only */
export const shellHeaderChrome = {
  backgroundColor: components.shell.headerBackground,
  // No hard border: AppShell draws a scroll-aware hairline (KAN-900).
  boxShadow: shadow.none,
  backdropFilter: 'none',
  /** Base rhythm + notch/status inset (env is often 0 in desktop emulators / RDM). */
  paddingTop: `calc(${spacePx[2]} + env(safe-area-inset-top, 0px))`,
  /** Matches paddingTop rhythm so titles + account menu don't sit flush against
      the bottom border. Mobile gets safe-area on top only — no need at bottom. */
  paddingBottom: spacePx[2],
};

/** Mobile product bar — layout/typography aligned with AdminShellHeader */
export const roleShellHeaderMobileCenter = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
};

export function roleShellBrandAreaMobile() {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: spacePx[2],
    minWidth: 0,
    flex: '0 0 auto',
  };
}

export function roleShellMobileMenuButton(isMobile) {
  return {
    appearance: 'none',
    WebkitAppearance: 'none',
    width: isMobile ? '34px' : '38px',
    height: isMobile ? '34px' : '38px',
    minWidth: 'unset',
    padding: 0,
    borderRadius: structure.radii.md,
    backgroundColor: color.neutral.surface,
    border: `1px solid ${semantic.border.default}`,
    color: color.neutral.textMuted,
    fontSize: isMobile ? fontSize.md : fontSize.lg,
    boxShadow: 'none',
    flexShrink: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

export function roleShellHeaderSectionTitle(_isMobile) {
  return {
    margin: 0,
    padding: 0,
    color: color.neutral.text,
    fontFamily: fontFamily.sans,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semibold,
    letterSpacing: '-0.025em',
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
  };
}

export function roleShellHeaderMetaCompany(isMobile) {
  return {
    border: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    font: 'inherit',
    fontFamily: fontFamily.sans,
    color: color.neutral.textSecondary,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.sm,
    letterSpacing: '0',
    maxWidth: isMobile ? 'min(52vw, 200px)' : 'min(28vw, 220px)',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
}

/** @deprecated use shellHeaderMetaType */
export const roleShellHeaderMetaType = shellHeaderMetaType;

/** Full header row on small viewports: admin-style padding and border */
export function roleShellHeaderRowMobilePadded() {
  return {
    ...shellHeaderChrome,
    ...shellHeaderRow,
    backgroundColor: color.neutral.surface,
    boxShadow: 'none',
    backdropFilter: 'none',
    paddingTop: cssMobileShellHeaderPaddingTop(),
    paddingRight: spacePx[4],
    paddingBottom: spacePx[2],
    paddingLeft: spacePx[4],
    gap: spacePx[2],
    fontFamily: fontFamily.sans,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  };
}

/**
 * Mobile product bar shell — used by MobileShellHeader and in-content headers.
 * @param {{ sticky?: boolean }} opts — sticky only for a header inside a scroll
 *   surface, never an AppShell slot header: inside MobileNavShell's transformed
 *   card, sticky splits into its own compositor layer and drifts vertically
 *   during push-nav. (The one in-content caller that used `sticky: true` —
 *   AccountTabMobileHeader — was deleted 2026-09-25 when Account merged onto
 *   ONE build for phone and desktop; MobileShellHeader is the sole caller now
 *   and always passes the sticky=false default.)
 */
export function mobileShellHeaderShell({ sticky = false } = {}) {
  return {
    backgroundColor: color.neutral.surface,
    boxShadow: shadow.none,
    paddingTop: cssMobileShellHeaderPaddingTop(),
    paddingRight: spacePx[3],
    paddingBottom: spacePx[2],
    paddingLeft: spacePx[3],
    fontFamily: fontFamily.sans,
    position: sticky ? 'sticky' : 'relative',
    ...(sticky ? { top: 0 } : {}),
    zIndex: 100,
    boxSizing: 'border-box',
  };
}

export const MOBILE_SHELL_HEADER_SIDE_PX = 44;

/** Equal side columns so the center title is mathematically centered. */
export function mobileShellHeaderGrid() {
  return {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    columnGap: spacePx[2],
    minHeight: `${MOBILE_SHELL_HEADER_SIDE_PX}px`,
  };
}

export function mobileShellHeaderSideLeft() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    justifySelf: 'start',
    minWidth: MOBILE_SHELL_HEADER_SIDE_PX,
    minHeight: MOBILE_SHELL_HEADER_SIDE_PX,
  };
}

export function mobileShellHeaderSideRight() {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    justifySelf: 'end',
    minWidth: MOBILE_SHELL_HEADER_SIDE_PX,
    minHeight: MOBILE_SHELL_HEADER_SIDE_PX,
  };
}

export function mobileShellHeaderTitleBlock() {
  return {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    paddingLeft: spacePx[1],
    paddingRight: spacePx[1],
  };
}

export function mobileShellHeaderSubtitle() {
  return {
    margin: 0,
    padding: 0,
    color: color.neutral.textMuted,
    fontFamily: fontFamily.sans,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    letterSpacing: '0.01em',
    lineHeight: 1.2,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

export function mobileShellHeaderTitle() {
  return {
    margin: 0,
    padding: 0,
    color: color.neutral.text,
    fontFamily: fontFamily.sans,
    fontSize: fontSize.xl,
    // Hanken Grotesk is a variable font (300 700) so 650 renders true.
    fontWeight: 650,
    letterSpacing: '-0.025em',
    lineHeight: 1.15,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
  };
}

/** Ghost nav toggle — icon only, no bordered box (Claude-style). */
export function mobileShellGhostMenuButton() {
  return {
    appearance: 'none',
    WebkitAppearance: 'none',
    width: MOBILE_SHELL_HEADER_SIDE_PX,
    height: MOBILE_SHELL_HEADER_SIDE_PX,
    minWidth: 'unset',
    padding: 0,
    margin: 0,
    border: 'none',
    borderRadius: structure.radii.lg,
    backgroundColor: 'transparent',
    color: color.neutral.text,
    boxShadow: 'none',
    flexShrink: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    WebkitTapHighlightColor: 'transparent',
  };
}

export const roleShellHeaderMetaShellMobile = {
  maxWidth: '100%',
};
